import type { NetworkEntry } from './types';

// A request taking this long is "slow" for the user — a product definition, not
// an artifact filter (the artifact filter is Resource Timing + the visibility guard).
const SLOW_THRESHOLD_MS = 2000;
// Clock-skew slack when matching a Resource Timing entry to a request's start.
const RT_MATCH_SLACK_MS = 50;

// Real per-request timing the browser already measured. Far more reliable than
// wall-clock Date.now(), which is inflated when a backgrounded tab throttles the
// JS event loop and the fetch/XHR callback fires late on refocus (the source of
// the bogus "slow 200000ms" entries).
interface ResourceTimingInfo {
  duration: number;
  ttfb?: number;
  transferSize?: number;
  initiatorType?: string;
}

export interface NetworkSignal {
  method: string;
  url: string;
  status: number; // 0 = network failure
  start: number; // Date.now() when the request was issued
  completedAt: number; // Date.now() when the callback fired (fallback timing only)
  rt: ResourceTimingInfo | null;
  startedHidden: boolean; // tab already hidden when the request was issued
  lastHiddenAt: number | null; // epoch ms of the last tab→hidden, or null
}

/**
 * Pure decision: what (if anything) to report for one finished request.
 * - Timing comes from Resource Timing when present (real network time), else the
 *   wall-clock fallback.
 * - "slow" is SUPPRESSED for a request that overlapped a hidden tab — its
 *   duration is untrustworthy (the browser throttles background work).
 * - Errors (status >= 400 or a network failure) ALWAYS report — an HTTP failure
 *   is real regardless of timing or visibility.
 */
export function buildNetworkEntry(s: NetworkSignal): NetworkEntry | null {
  const isError = s.status >= 400 || s.status === 0;
  const duration = s.rt ? s.rt.duration : s.completedAt - s.start;
  // Hidden at issue, or hidden at any point after → its timing is untrustworthy.
  const overlappedHidden =
    s.startedHidden || (s.lastHiddenAt != null && s.lastHiddenAt >= s.start);
  const slow = !isError && duration >= SLOW_THRESHOLD_MS && !overlappedHidden;
  if (!isError && !slow) return null;

  return {
    method: s.method,
    url: s.url,
    status: s.status,
    duration: Math.round(duration),
    timestamp: s.start,
    ...(s.status === 0 ? { error: 'Network error' } : {}),
    ...(slow ? { slow: true } : {}),
    ...(s.rt?.ttfb != null ? { ttfb: Math.round(s.rt.ttfb) } : {}),
    ...(s.rt?.transferSize != null ? { transferSize: s.rt.transferSize } : {}),
    ...(s.rt?.initiatorType ? { initiatorType: s.rt.initiatorType } : {}),
  };
}

// Reads the browser's measured timing for the request, matched by absolute URL
// and start time (most recent matching entry). null when unavailable.
function findResourceTiming(
  url: string,
  start: number
): ResourceTimingInfo | null {
  try {
    const abs = new URL(url, location.href).href;
    const matches = (
      performance.getEntriesByName(
        abs,
        'resource'
      ) as PerformanceResourceTiming[]
    ).filter(
      (e) => performance.timeOrigin + e.startTime >= start - RT_MATCH_SLACK_MS
    );
    const last = matches[matches.length - 1];
    if (!last) return null;
    return {
      duration: last.duration,
      ttfb:
        last.responseStart > last.startTime
          ? last.responseStart - last.startTime
          : undefined,
      transferSize: last.transferSize || undefined,
      initiatorType: last.initiatorType || undefined,
    };
  } catch {
    return null;
  }
}

type Emit = (
  method: string,
  url: string,
  status: number,
  start: number,
  startedHidden: boolean
) => void;

export function startNetworkCapture(
  callback: (entry: NetworkEntry) => void,
  excludeUrl?: string
): () => void {
  let lastHiddenAt: number | null = null;
  const onVisibility = (): void => {
    if (document.hidden) lastHiddenAt = Date.now();
  };
  document.addEventListener('visibilitychange', onVisibility);

  const emit: Emit = (method, url, status, start, startedHidden) => {
    const entry = buildNetworkEntry({
      method,
      url,
      status,
      start,
      completedAt: Date.now(),
      rt: findResourceTiming(url, start),
      startedHidden,
      lastHiddenAt,
    });
    if (entry) {
      try {
        callback(entry);
      } catch {
        // noop
      }
    }
  };

  const cleanupFetch = patchFetch(emit, excludeUrl);
  const cleanupXhr = patchXhr(emit, excludeUrl);

  return () => {
    document.removeEventListener('visibilitychange', onVisibility);
    cleanupFetch();
    cleanupXhr();
  };
}

function shouldExclude(url: string, excludeUrl?: string): boolean {
  return !!excludeUrl && url.startsWith(excludeUrl);
}

function patchFetch(emit: Emit, excludeUrl?: string): () => void {
  const originalFetch = window.fetch;

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url =
      typeof input === 'string'
        ? input
        : input instanceof URL
        ? input.href
        : input.url;

    if (shouldExclude(url, excludeUrl)) {
      return originalFetch.call(window, input, init);
    }

    const method = (init?.method ?? 'GET').toUpperCase();
    const start = Date.now();
    const startedHidden = document.hidden;

    try {
      const response = await originalFetch.call(window, input, init);
      emit(method, url, response.status, start, startedHidden);
      return response;
    } catch (err) {
      emit(method, url, 0, start, startedHidden);
      throw err;
    }
  };

  return () => {
    window.fetch = originalFetch;
  };
}

function patchXhr(emit: Emit, excludeUrl?: string): () => void {
  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (
    method: string,
    url: string | URL,
    ...rest: unknown[]
  ) {
    (this as XhrWithMeta).__flamioMethod = method.toUpperCase();
    (this as XhrWithMeta).__flamioUrl =
      typeof url === 'string' ? url : url.href;
    return originalOpen.apply(this, [method, url, ...rest] as Parameters<
      typeof originalOpen
    >);
  };

  XMLHttpRequest.prototype.send = function (
    body?: Document | XMLHttpRequestBodyInit | null
  ) {
    const meta = this as XhrWithMeta;

    if (shouldExclude(meta.__flamioUrl ?? '', excludeUrl)) {
      return originalSend.call(this, body);
    }

    const start = Date.now();
    const startedHidden = document.hidden;
    this.addEventListener('loadend', () => {
      emit(
        meta.__flamioMethod ?? 'UNKNOWN',
        meta.__flamioUrl ?? '',
        this.status,
        start,
        startedHidden
      );
    });
    return originalSend.call(this, body);
  };

  return () => {
    XMLHttpRequest.prototype.open = originalOpen;
    XMLHttpRequest.prototype.send = originalSend;
  };
}

interface XhrWithMeta extends XMLHttpRequest {
  __flamioMethod?: string;
  __flamioUrl?: string;
}
